import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { ConfirmModal, LoadingState } from '../../../components/admin/shared';
import { Mail, Users, Send, CheckCircle, AlertCircle } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const BulkNotifications = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [recipientFilter, setRecipientFilter] = useState({
    all: false,
    roles: [],
    userIds: [],
    service: ''
  });

  const [notification, setNotification] = useState({
    title: '',
    message: '',
    type: 'info',
    actionUrl: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadUserStats();
  }, []);

  const loadUserStats = async () => {
    try {
      const response = await educentialApi.get('/api/admin/users?limit=1');
      setUserStats({
        total: response.data.pagination?.total || 0,
        students: 0, // Could enhance with role-specific counts
        educators: 0,
        admins: 0
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    const hasRecipients = recipientFilter.all ||
                         recipientFilter.roles.length > 0 ||
                         recipientFilter.userIds.length > 0;

    if (!hasRecipients) {
      newErrors.recipients = 'Please select at least one recipient group';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!notification.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!notification.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRoleToggle = (role) => {
    setRecipientFilter(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleAllToggle = () => {
    setRecipientFilter(prev => ({
      ...prev,
      all: !prev.all,
      roles: prev.all ? prev.roles : []
    }));
  };

  const getRecipientCount = () => {
    if (recipientFilter.all) {
      return userStats?.total || 0;
    }
    // Simplified - in reality would calculate based on selected roles
    return recipientFilter.roles.length * 10; // Placeholder
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSend = async () => {
    setShowConfirmModal(false);
    setLoading(true);

    try {
      await educentialApi.post('/api/admin/operations/bulk-notify', {
        recipientFilter,
        notification
      });

      setSuccess(true);
      // Reset form after success
      setTimeout(() => {
        setStep(1);
        setRecipientFilter({ all: false, roles: [], userIds: [], service: '' });
        setNotification({ title: '', message: '', type: 'info', actionUrl: '' });
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error sending notifications:', error);
      alert('Failed to send notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Bulk Notifications">
        <LoadingState message="Sending notifications..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Bulk Notifications">
      <div className="p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Send Bulk Notifications</h2>
            <p className="text-gray-600 mt-1">Send targeted notifications to groups of users</p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-green-900 font-medium">Notifications sent successfully!</p>
                <p className="text-green-700 text-sm">
                  {getRecipientCount()} users will receive your notification.
                </p>
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Select Recipients' },
              { num: 2, label: 'Compose Message' },
              { num: 3, label: 'Review & Send' }
            ].map((s, index) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {s.num}
                  </div>
                  <span className={`text-sm font-medium ${step >= s.num ? 'text-gray-900' : 'text-gray-500'}`}>
                    {s.label}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`flex-1 h-1 mx-4 ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Select Recipients */}
          {step === 1 && (
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Who should receive this notification?</h3>

              {/* All Users Option */}
              <label className="flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={recipientFilter.all}
                  onChange={handleAllToggle}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">All Users</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Send to all {userStats?.total || 0} registered users
                  </p>
                </div>
              </label>

              {/* Role-based Selection */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Or select specific roles:</p>
                <div className="space-y-2">
                  {[
                    { value: 'STUDENT', label: 'Students', description: 'All student users' },
                    { value: 'EDUCATOR', label: 'Educators', description: 'All educator users' },
                    { value: 'ADMIN', label: 'Admins', description: 'All admin users' }
                  ].map((role) => (
                    <label
                      key={role.value}
                      className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        recipientFilter.roles.includes(role.value)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={recipientFilter.roles.includes(role.value)}
                        onChange={() => handleRoleToggle(role.value)}
                        disabled={recipientFilter.all}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{role.label}</span>
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {errors.recipients && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.recipients}</span>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Estimated recipients: <span className="font-semibold">{getRecipientCount()}</span>
                </p>
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Compose Message */}
          {step === 2 && (
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Compose Your Notification</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={notification.title}
                  onChange={(e) => setNotification({ ...notification, title: e.target.value })}
                  className={`w-full border rounded-lg px-4 py-2 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Important Update"
                  maxLength={100}
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                <p className="text-xs text-gray-500 mt-1">{notification.title.length}/100 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={notification.message}
                  onChange={(e) => setNotification({ ...notification, message: e.target.value })}
                  rows={6}
                  className={`w-full border rounded-lg px-4 py-2 ${
                    errors.message ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Write your notification message here..."
                  maxLength={500}
                />
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                <p className="text-xs text-gray-500 mt-1">{notification.message.length}/500 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Type
                </label>
                <select
                  value={notification.type}
                  onChange={(e) => setNotification({ ...notification, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                >
                  <option value="info">Information</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="error">Alert</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action URL (Optional)
                </label>
                <input
                  type="url"
                  value={notification.actionUrl}
                  onChange={(e) => setNotification({ ...notification, actionUrl: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  placeholder="https://example.com/action"
                />
                <p className="text-xs text-gray-500 mt-1">Link where users will be directed when clicking the notification</p>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Back
                </button>
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Send */}
          {step === 3 && (
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Review Your Notification</h3>

              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Recipients</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {recipientFilter.all ? 'All Users' : recipientFilter.roles.join(', ')}
                    ({getRecipientCount()} users)
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-600">Notification Preview</p>
                  <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-700 mt-1">{notification.message}</p>
                        {notification.actionUrl && (
                          <a href="#" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                            View Details →
                          </a>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        notification.type === 'info' ? 'bg-blue-100 text-blue-800' :
                        notification.type === 'success' ? 'bg-green-100 text-green-800' :
                        notification.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {notification.type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Important</p>
                    <p className="text-sm text-yellow-800 mt-1">
                      This notification will be sent to {getRecipientCount()} users and cannot be undone.
                      Please review carefully before sending.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Back
                </button>
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Notification</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={showConfirmModal}
          title="Send Bulk Notification?"
          message={`Are you sure you want to send this notification to ${getRecipientCount()} users? This action cannot be undone.`}
          confirmText="Send"
          cancelText="Cancel"
          onConfirm={handleSend}
          onCancel={() => setShowConfirmModal(false)}
        />
      </div>
    </AdminLayout>
  );
};

export default BulkNotifications;
